import { Component, inject, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { AdminDistrictCalendar } from 'app/admin/services/districts-data/models/admin-district-calendars.model';
import { LocalizedToasterService } from 'app/core/toaster/localized-toaster.service';
import { DataTrackLabelEnum } from 'app/shared/enums/data-track-label.enum';
import { DistrictCalendarService } from 'app/shared/services/district-calendar.service';
import { KhAnalyticsService } from 'app/shared/services/kh-analytics.service';

import 'app/core/forms/form-builder.extensions';

@UntilDestroy()
@Component({
  templateUrl: 'calendar-edit.component.html',
  standalone: false,
  styleUrls: ['calendar-edit.component.less']
})
export class CalendarEditComponent implements OnInit {
  private readonly toaster = inject(LocalizedToasterService);
  private readonly districtCalendarService = inject(DistrictCalendarService);
  private readonly khAnalyticsService = inject(KhAnalyticsService);

  public calendars: AdminDistrictCalendar[];
  public modalRef: { close: (result?: any) => void; dismiss: () => void } | null = null;
  public result: string | null = null;
  public form!: FormGroup;
  public selectedCalendarId = '';
  public selectedMonth = 'Month';
  public selectedDay = 'Day';
  public selectedYear: number = new Date().getFullYear();

  public ngOnInit(): void {
    this.districtCalendarService.getAllCalendars()
      .pipe(untilDestroyed(this))
      .subscribe((calendars) => {
        this.calendars = calendars;
      });
  }

  public save(): void {
    if (!this.selectedCalendarId || !this.selectedMonth || !this.selectedDay) {
      this.toaster.error('Please select calendar, month, and day.');
      return;
    }

    if (this.calendars.some((c) => c.name === this.selectedCalendarId)) {
      this.toaster.error('A calendar with this name already exists.');
      return;
    }

    this.districtCalendarService
      .addNewCalendar(this.selectedCalendarId, String(this.selectedMonth), this.selectedDay)
      .pipe(untilDestroyed(this))
      .subscribe((result) => {
        if (result && result.errors) {
          this.toaster.error('Error', Object.values(result.errors).flat().join(', '));
        }
        else {
          this.toaster.success('Success!', 'Calendar added');

          // Track analytics event for calendar creation
          this.khAnalyticsService.trackEventSimple(
            DataTrackLabelEnum.AdminCalendarCreated,
            undefined,
            {
              CalendarId: this.selectedCalendarId
            }
          );

          this.districtCalendarService.getAllCalendars()
            .pipe(untilDestroyed(this))
            .subscribe((calendars) => {
              this.calendars = calendars;
            });

          this.selectedCalendarId = '';
          this.selectedMonth = 'Month';
          this.selectedDay = 'Day';
        }
      });
  }

  public onMonthChange(month: string) {
    this.selectedMonth = month;
  }

  public getDays(): string[] {
    const daysInMonth = new Date(this.selectedYear, Number(this.selectedMonth), 0).getDate();
    return ['Day', ...Array.from({ length: daysInMonth }, (_, i) => String(i + 1))];
  }
}
